import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import copyIcon from '@/assets/icons/copyNarratives.svg';
import deleteIcon from '@/assets/icons/deleteIcon.svg';
import editIcon from '@/assets/icons/editIcon.svg';
import { SmoothMarkdown } from '@/components/SmoothMarkdown';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { displayEmail } from '@/config/office/office';
import { useCopyToClipboard } from '@/utils/hooks/useCopyToClipboard';
import { cn } from '@/utils/utils';

export interface NarrativeCardData {
  content: string;
  graphMessageId?: string; // Graph API message ID for opening email in Outlook
  id: string;
  senderColor: string;
  senderInitial: string;
  senderName: string;
  subject: string;
  timestamp: string;
}

interface NarrativeCardProps {
  className?: string;
  isSelected?: boolean;
  narrative: NarrativeCardData;
  onCopySuccess?: (id: string) => void;
  onDelete?: (id: string) => void;
  onSave?: (id: string, content: string) => void;
}

export function NarrativeCard({
  className,
  isSelected = false,
  narrative,
  onCopySuccess,
  onDelete,
  onSave,
}: NarrativeCardProps) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(narrative.content);
  const { copied, copyToClipboard } = useCopyToClipboard({
    successMessage: t('narrative.cards.copied'),
  });

  const handleEdit = () => {
    setIsEditing(true);
    setEditedContent(narrative.content);
  };

  const handleSave = () => {
    if (onSave) {
      onSave(narrative.id, editedContent);
    }
    setIsEditing(false);
  };

  const handleCopy = () => {
    void copyToClipboard(narrative.content, {
      onSuccess: () => onCopySuccess?.(narrative.id),
    });
  };

  const handleDelete = () => {
    onDelete?.(narrative.id);
  };

  const handleViewSourceMail = async () => {
    if (narrative.graphMessageId) {
      try {
        await displayEmail(narrative.graphMessageId);
      } catch (error) {
        console.error('Failed to open email in Outlook:', error);
        toast.error(t('narrative.viewMail.failed'));
      }
    }
  };

  return (
    <div
      className={cn(
        'w-full rounded-lg border bg-card px-3 py-3 transition-colors',
        isSelected ? 'border-dashed border-info' : 'border-border-subtle',
        className,
      )}
    >
      {/* Header: Avatar + Name + Subject + Edit Icon */}
      <div className="flex items-center gap-2.5">
        {/* Avatar + Name + Subject with background */}
        <TooltipProvider delay={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="bg-back-bg2 hover:bg-elevated-subtle flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-1 transition-colors"
                onClick={handleViewSourceMail}
              >
                {/* Avatar */}
                <div
                  className={`flex h-5.5 w-5.5 shrink-0 items-center justify-center rounded-full text-[10px] font-medium text-white ${narrative.senderColor}`}
                >
                  {narrative.senderInitial}
                </div>

                {/* Sender Name + Subject */}
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <span className="text-foreground font-['Inter'] text-[12px] leading-normal font-semibold tracking-normal">
                    {narrative.senderName}
                  </span>
                  <span className="text-tertiary min-w-0 flex-1 truncate font-['Inter'] text-[12px] leading-normal font-normal tracking-normal">
                    {narrative.subject}
                  </span>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p>{t('narrative.card.viewSource')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Edit Icon - outside the background */}
        <TooltipProvider delay={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={t('narrative.card.editAria')}
                className="hover:bg-elevated-subtle h-6 w-6 shrink-0 rounded-full bg-transparent p-0"
                onClick={handleEdit}
                size="icon"
                variant="ghost"
              >
                <img
                  alt={t('narrative.card.editAlt')}
                  className="icon-themed h-4 w-4"
                  src={editIcon}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p>{t('narrative.card.editLabel')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Body: Content */}
      {isEditing ? (
        <Textarea
          autoFocus
          className="custom-scrollbar border-border-subtle bg-back-bg text-secondary focus:border-border-subtle mt-2 max-h-75 min-h-30 resize-none overflow-y-auto rounded-md border-[0.5px] p-2 font-['Inter'] text-[12px] leading-normal tracking-normal outline-none focus:shadow-none focus:ring-0 focus:outline-none focus-visible:ring-0"
          onChange={(e) => setEditedContent(e.target.value)}
          value={editedContent}
        />
      ) : (
        <div className="text-secondary mt-2 font-['Inter'] text-[12px] leading-normal tracking-normal">
          <SmoothMarkdown content={narrative.content} />
        </div>
      )}

      {/* Footer: Timestamp + Actions */}
      {isEditing ? (
        <div className="mt-3 flex items-center justify-between">
          <span className="text-neutral-mid font-['Inter'] text-[11px]">{narrative.timestamp}</span>
          <Button
            aria-label={t('narrative.card.saveAria')}
            className="bg-primary text-primary-foreground hover:bg-primary-hover h-auto rounded-md px-4 py-1 text-[12px] leading-tight font-medium"
            onClick={handleSave}
          >
            {t('narrative.card.save')}
          </Button>
        </div>
      ) : (
        <div className="mt-3 flex items-center justify-between">
          <span className="text-neutral-mid font-['Inter'] text-[11px]">{narrative.timestamp}</span>
          <div className="flex items-center gap-2">
            {onCopySuccess !== undefined && (
              <TooltipProvider delay={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      aria-label={t('narrative.card.copyAria')}
                      className="hover:bg-elevated-subtle h-6 w-6 rounded-full bg-transparent p-0"
                      onClick={handleCopy}
                      size="icon"
                      variant="ghost"
                    >
                      <img
                        alt={t('narrative.card.copyAlt')}
                        className="icon-themed h-4.5 w-4.5"
                        src={copyIcon}
                      />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>
                      {copied ? t('narrative.card.copiedLabel') : t('narrative.card.copyLabel')}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {onDelete && (
              <TooltipProvider delay={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      aria-label={t('narrative.card.deleteAria')}
                      className="hover:bg-elevated-subtle h-6 w-6 rounded-full bg-transparent p-0"
                      onClick={handleDelete}
                      size="icon"
                      variant="ghost"
                    >
                      <img
                        alt={t('narrative.card.deleteAlt')}
                        className="icon-themed h-4.5 w-4.5"
                        src={deleteIcon}
                      />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>{t('narrative.card.deleteLabel')}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function NarrativeCardSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'w-full rounded-lg border border-border-subtle bg-card px-4 py-4 shadow-[0_10px_30px_rgba(0,0,0,0.03)]',
        className,
      )}
    >
      {/* Top row: long line + right square */}
      <div className="mb-3 flex items-start justify-between">
        <Skeleton className="h-4 w-[78%]" />
        <Skeleton className="h-7 w-7 rounded-md" />
      </div>

      {/* Middle lines */}
      <div className="mt-3 space-y-2">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-[85%]" />
      </div>

      {/* Bottom row: two pills on left, two squares on right */}
      <div className="mt-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-20" />
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="h-7 w-7 rounded-md" />
          <Skeleton className="h-7 w-7 rounded-md" />
        </div>
      </div>
    </div>
  );
}
